import type { RequiredWorkOrderField } from '@atlas/types'
import { SectionCard } from '@atlas/ui-components'
import { CircleAlert, CircleCheck } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { FromWorkfrontBadge } from '@/components/work-orders/WorkfrontBadges'
import { REQUIRED_FIELD_LABEL } from '@/domain/required-fields'

/** One field the request must carry, with the value it carries, or null when it is missing. */
export interface RequiredFieldRow {
	field: RequiredWorkOrderField
	value: string | null
}

export interface VerifyRequestCardProps {
	rows: readonly RequiredFieldRow[]
	/** For an order synced from Workfront: what Workfront says about it, and the refresh. Its values are read-only here. */
	workfront?: ReactNode
	/** For a manual order: where its values are edited. */
	editHref?: string
}

function Row({ field, value, fromWorkfront }: RequiredFieldRow & { fromWorkfront: boolean }) {
	const label = REQUIRED_FIELD_LABEL[field]
	return (
		<li className="flex items-center justify-between gap-3 py-2">
			<span className="inline-flex items-center gap-2 text-sm text-ds-text">
				{value ? (
					<CircleCheck className="h-4 w-4 text-ds-success" aria-hidden />
				) : (
					<CircleAlert className="h-4 w-4 text-ds-warning" aria-hidden />
				)}
				{label}
			</span>
			{value ? (
				<span className="inline-flex min-w-0 items-center gap-2 text-sm font-semibold text-ds-text">
					<span className="truncate">{value}</span>
					{fromWorkfront ? <FromWorkfrontBadge compact /> : null}
				</span>
			) : (
				<span className="text-sm font-semibold text-ds-warning">Missing</span>
			)}
		</li>
	)
}

/** The PM's check on a created order: every field production needs, present or missing, and where to fix a missing one. */
export function VerifyRequestCard({ rows, workfront, editHref }: VerifyRequestCardProps) {
	const fromWorkfront = workfront !== undefined
	const hint = fromWorkfront
		? 'A missing or wrong value is fixed in Workfront, then refreshed here.'
		: 'A missing or wrong value is fixed by editing the order.'
	return (
		<SectionCard title="Verify request" aria-label="Verify request" collapsible>
			<p className="text-sm text-ds-text-muted">{hint}</p>
			<ul className="mt-3 divide-y divide-ds-border">
				{rows.map((row) => (
					<Row key={row.field} {...row} fromWorkfront={fromWorkfront} />
				))}
			</ul>
			{workfront ? <div className="mt-2">{workfront}</div> : null}
			{editHref ? (
				<Link to={editHref} className="mt-4 inline-block text-sm text-ds-primary hover:underline">
					Edit the order
				</Link>
			) : null}
		</SectionCard>
	)
}
